import { useEffect, useMemo, useRef, useState } from "react";
import {
  AlertTriangle,
  ArrowLeft,
  Eye,
  LayoutTemplate,
  Loader2,
  PencilRuler,
  Save,
  Sparkles,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { BuilderDocumentPreview } from "./BuilderDocumentPreview";
import { ProposalBuilderCanvas } from "./ProposalBuilderCanvas";
import { useProposal, useUpdateProposal } from "../../hooks/useProposals";
import { ProposalDetail } from "../dashboard/ProposalDetail";
import type { Json } from "@/lib/database.types";
import type { BuilderMode, ProposalDocumentLayout } from "../../types";
import type { LineItemUpdate } from "./BuilderElementPreview";
import { supabase } from "@/lib/supabase";
import { useQueryClient } from "@tanstack/react-query";

interface ProposalBuilderEditorProps {
  proposalId: string;
}

type CenterViewMode = "canvas" | "preview";

function normalizeLayout(value: unknown): ProposalDocumentLayout {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return { pages: [] };
  }

  const record = value as Record<string, unknown>;
  const pages = Array.isArray(record.pages) ? record.pages : [];

  return {
    ...record,
    pages,
  } as ProposalDocumentLayout;
}

function areLayoutsEqual(
  a: ProposalDocumentLayout | null,
  b: ProposalDocumentLayout | null
) {
  return JSON.stringify(a ?? { pages: [] }) === JSON.stringify(b ?? { pages: [] });
}

function getPageTitle(page: unknown, index: number) {
  if (!page || typeof page !== "object" || Array.isArray(page)) {
    return `Page ${index + 1}`;
  }

  const record = page as Record<string, unknown>;
  const title =
    typeof record.title === "string" && record.title.trim().length > 0
      ? record.title.trim()
      : null;
  const pageType =
    typeof record.pageType === "string" && record.pageType.trim().length > 0
      ? record.pageType.trim()
      : typeof record.page_type === "string" && record.page_type.trim().length > 0
        ? record.page_type.trim()
        : null;

  if (title) {
    return title;
  }

  if (pageType) {
    return pageType
      .replace(/[_-]/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());
  }

  return `Page ${index + 1}`;
}

export function ProposalBuilderEditor({
  proposalId,
}: ProposalBuilderEditorProps) {
  const { data: proposal, isLoading, isError } = useProposal(proposalId);

  const template = proposal?.template ?? null;

  // Prefer the proposal's own layout_json when it has pages.
  // Fall back to the shared template layout for legacy proposals or when
  // the proposal's layout is empty (pages: []) — this handles proposals
  // created before DnD content was built into the template.
  const serverLayout = useMemo(() => {
    const proposalLayout = normalizeLayout(proposal?.layout_json);
    if (proposalLayout.pages.length > 0) return proposalLayout;
    return normalizeLayout(template?.layout_json);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposal?.layout_json, template?.layout_json]);

  const [draftLayout, setDraftLayout] = useState<ProposalDocumentLayout>({
    pages: [],
  });
  const [selectedPageIndex, setSelectedPageIndex] = useState(0);
  const [centerViewMode, setCenterViewMode] = useState<CenterViewMode>("canvas");

  useEffect(() => {
    setDraftLayout(serverLayout);
  }, [serverLayout]);

  const pages = Array.isArray(draftLayout.pages) ? draftLayout.pages : [];

  useEffect(() => {
    if (pages.length === 0) {
      setSelectedPageIndex(0);
      return;
    }

    if (selectedPageIndex > pages.length - 1) {
      setSelectedPageIndex(pages.length - 1);
    }
  }, [pages.length, selectedPageIndex]);

  const hasUnsavedChanges = useMemo(
    () => !areLayoutsEqual(serverLayout, draftLayout),
    [serverLayout, draftLayout]
  );

  const selectedPage = pages[selectedPageIndex] ?? null;
  const selectedPageTitle = selectedPage
    ? getPageTitle(selectedPage, selectedPageIndex)
    : "No page selected";

  const builderMode: BuilderMode = template?.builder_mode ?? "drag_drop";

  // Build proposal context to thread prospect + line-item data down to canvas elements
  const proposalContext = useMemo(() => {
    if (!proposal) return undefined;
    const lineItems = (proposal.proposal_line_items ?? []).map((li) => ({
      id: li.id,
      name: li.name ?? "",
      description: li.description ?? null,
      price: li.price ?? null,
      quantity: li.quantity ?? null,
      price_unit: li.price_unit ?? null,
    }));
    return {
      proposalId: proposal.id,
      prospectName: proposal.prospect_name ?? null,
      prospectAddress: [
        proposal.prospect_address,
        proposal.prospect_city,
        proposal.prospect_state,
        proposal.prospect_zip,
      ].filter(Boolean).join(", ") || null,
      lineItems,
      subtotal: proposal.subtotal ?? null,
      total: proposal.total_value ?? null,
    };
  }, [proposal]);

  const inheritedPages = useMemo(() => {
    const rows = (proposal as unknown as Record<string, unknown>)?.proposal_static_pages;
    if (!Array.isArray(rows)) return [];
    return (rows as Array<{
      page_order?: number | null;
      static_pages?: { id?: string; title?: string | null; content_html?: string | null } | null;
    }>)
      .sort((a, b) => (a.page_order ?? 0) - (b.page_order ?? 0))
      .map(row => ({
        id: row.static_pages?.id ?? "",
        title: row.static_pages?.title ?? "Untitled",
        contentHtml: row.static_pages?.content_html ?? "",
      }))
      .filter(p => p.id);
  }, [proposal]);

  const updateProposal = useUpdateProposal();
  const qc = useQueryClient();
  const [activeTab, setActiveTab] = useState<"builder" | "details">("builder");
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const initialTabSet = useRef(false);
  const autoSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isAutoSaving, setIsAutoSaving] = useState(false);

  // For prebuilt proposals, land on Details & Send instead of the canvas
  useEffect(() => {
    if (!proposal || initialTabSet.current) return;
    initialTabSet.current = true;
    if (builderMode === "prebuilt") setActiveTab("details");
  }, [proposal, builderMode]);

  // Auto-save 2.5s after the last canvas change so regenerating PDF always
  // uses the latest layout without requiring a manual save click.
  useEffect(() => {
    if (!hasUnsavedChanges) return;
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(async () => {
      setIsAutoSaving(true);
      await handleSave();
      setIsAutoSaving(false);
    }, 2500);
    return () => {
      if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftLayout]);

  async function handleSave(): Promise<boolean> {
    if (!hasUnsavedChanges) return true;
    try {
      setSaveError(null);
      setSaveSuccess(false);
      await updateProposal.mutateAsync({
        id: proposalId,
        updates: { layout_json: draftLayout as unknown as Json },
      });
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
      return true;
    } catch (err) {
      const pgErr = err as { message?: string; details?: string; hint?: string; code?: string } | null;
      const message =
        pgErr?.message ||
        (err instanceof Error ? err.message : null) ||
        "Failed to save layout.";
      const detail = pgErr?.details ? ` (${pgErr.details})` : pgErr?.hint ? ` — ${pgErr.hint}` : "";
      setSaveError(message + detail);
      return false;
    }
  }

  async function handleSwitchToDetails() {
    if (hasUnsavedChanges) {
      const ok = await handleSave();
      if (!ok) return; // don't switch tabs if save failed
    }
    setActiveTab("details");
  }

  async function handleUpdateLineItem(id: string, updates: LineItemUpdate) {
    const row: Record<string, unknown> = {};
    if (updates.name !== undefined)         row.name         = updates.name;
    if (updates.price !== undefined)        row.price        = updates.price;
    if (updates.price_unit !== undefined)   row.price_unit   = updates.price_unit;
    if (updates.square_feet !== undefined)  row.square_feet  = updates.square_feet;
    if (Object.keys(row).length === 0) return;
    await supabase.from("proposal_line_items").update(row).eq("id", id);
    qc.invalidateQueries({ queryKey: ["proposals", "detail", proposalId] });
  }

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0b0b0c] text-white">
        <div className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.03] px-5 py-4">
          <Loader2 className="h-5 w-5 animate-spin text-white/45" />
          <span className="text-sm text-white/70">Loading builder...</span>
        </div>
      </div>
    );
  }

  if (isError || !proposal) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0b0b0c] px-6 text-white">
        <div className="max-w-md rounded-3xl border border-red-500/20 bg-red-500/10 p-6 text-center">
          <AlertTriangle className="mx-auto h-6 w-6 text-red-400" />
          <h1 className="mt-3 text-lg font-semibold">Unable to load builder</h1>
          <p className="mt-2 text-sm leading-6 text-white/55">
            The proposal builder could not load this proposal right now.
          </p>
        </div>
      </div>
    );
  }

  if (!template) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0b0b0c] px-6 text-white">
        <div className="max-w-lg rounded-3xl border border-amber-500/20 bg-amber-500/10 p-6 text-center">
          <AlertTriangle className="mx-auto h-6 w-6 text-amber-300" />
          <h1 className="mt-3 text-lg font-semibold">Builder template not found</h1>
          <p className="mt-2 text-sm leading-6 text-white/60">
            This proposal does not currently have a hydrated template record attached,
            so the drag-and-drop builder cannot load its layout source yet.
          </p>
        </div>
      </div>
    );
  }

  // ── Details & Send tab ──────────────────────────────────────────────────────
  if (activeTab === "details") {
    return (
      <div className="min-h-screen bg-[#0b0b0c] text-white">
        <div className="sticky top-0 z-40 border-b border-white/10 bg-[#0f1012]/95 px-4 py-3 lg:px-6">
          <button
            type="button"
            onClick={() => setActiveTab("builder")}
            className="inline-flex items-center gap-2 text-sm text-white/55 transition hover:text-white"
          >
            <ArrowLeft className="h-4 w-4" />
            {builderMode === "prebuilt" ? "Edit in builder" : "Back to builder"}
          </button>
        </div>
        <ProposalDetail proposalId={proposalId} onBeforeRegenerate={handleSave} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0b0b0c] text-white">
      <div className="border-b border-white/10 bg-[#0f1012]/95 backdrop-blur">
        <div className="mx-auto max-w-[2200px] px-4 py-4 lg:px-6">
          <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-[0.2em] text-white/35">
                Proposal Builder
              </p>

              <div className="mt-2 flex min-w-0 items-center gap-2">
                <LayoutTemplate className="h-4 w-4 shrink-0 text-white/45" />
                <h1 className="truncate text-2xl font-semibold tracking-tight">
                  {proposal.prospect_name || "Untitled Proposal"}
                </h1>
              </div>

              <p className="mt-2 max-w-5xl text-sm leading-6 text-white/42">
                Build visually on the canvas first, then switch to preview to review
                the full assembled proposal.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {/* Tab toggle */}
              <div className="inline-flex rounded-2xl border border-white/10 bg-black/20 p-1">
                <button
                  type="button"
                  onClick={() => setActiveTab("builder")}
                  className={cn(
                    "rounded-xl px-3 py-1.5 text-xs transition",
                    activeTab === "builder"
                      ? "bg-white font-medium text-[#0b0b0c]"
                      : "text-white/55 hover:text-white/80",
                  )}
                >
                  Builder
                </button>
                <button
                  type="button"
                  onClick={handleSwitchToDetails}
                  disabled={updateProposal.isPending}
                  className={cn(
                    "rounded-xl px-3 py-1.5 text-xs transition disabled:opacity-40",
                    builderMode === "prebuilt"
                      ? "bg-white font-medium text-[#0b0b0c]"
                      : "text-white/55 hover:text-white/80",
                  )}
                >
                  Details & Send
                </button>
              </div>

              {/* Save button */}
              {true && (
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={!hasUnsavedChanges || updateProposal.isPending || isAutoSaving}
                  className={cn(
                    "inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs transition disabled:opacity-40",
                    hasUnsavedChanges || isAutoSaving
                      ? "border-white/20 bg-white/[0.08] text-white hover:bg-white/[0.14]"
                      : "border-emerald-500/20 bg-emerald-500/10 text-emerald-200",
                  )}
                >
                  {updateProposal.isPending || isAutoSaving ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Save className="h-3.5 w-3.5" />
                  )}
                  {updateProposal.isPending || isAutoSaving
                    ? "Saving…"
                    : hasUnsavedChanges
                    ? "Save layout"
                    : "Saved"}
                </button>
              )}

              {/* Builder mode badge */}
              <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-3 py-1.5 text-xs text-white/65">
                <Sparkles className="h-3.5 w-3.5" />
                {builderMode === "drag_drop" ? "Drag & Drop" : builderMode}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-[2200px] px-2 py-3 md:px-3 lg:px-4 xl:px-5">
        <div className="overflow-hidden rounded-[30px] border border-white/10 bg-white/[0.03]">
          <div className="flex flex-col gap-4 border-b border-white/10 px-4 py-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <PencilRuler className="h-4 w-4 text-white/45" />
                <p className="text-sm font-medium text-white/90">
                  Visual Workspace
                </p>
              </div>
              <p className="mt-1 text-xs leading-5 text-white/40">
                {centerViewMode === "canvas"
                  ? `Working on ${selectedPageTitle}.`
                  : "Reviewing the full proposal stack."}
              </p>
            </div>

            <div className="inline-flex w-full rounded-2xl border border-white/10 bg-black/20 p-1 lg:w-auto">
              <button
                type="button"
                onClick={() => setCenterViewMode("canvas")}
                className={`flex-1 rounded-xl px-3 py-2 text-sm transition lg:flex-none ${
                  centerViewMode === "canvas"
                    ? "bg-white text-[#0b0b0c]"
                    : "text-white/55 hover:text-white/80"
                }`}
              >
                Canvas
              </button>
              <button
                type="button"
                onClick={() => setCenterViewMode("preview")}
                className={`flex-1 rounded-xl px-3 py-2 text-sm transition lg:flex-none ${
                  centerViewMode === "preview"
                    ? "bg-white text-[#0b0b0c]"
                    : "text-white/55 hover:text-white/80"
                }`}
              >
                Preview
              </button>
            </div>
          </div>

          {saveError && (
            <div className="border-b border-red-500/20 bg-red-500/10 px-4 py-3">
              <p className="text-xs leading-5 text-red-200">{saveError}</p>
            </div>
          )}

          {saveSuccess && !saveError && (
            <div className="border-b border-emerald-500/20 bg-emerald-500/10 px-4 py-3">
              <p className="text-xs leading-5 text-emerald-200">
                Layout saved — {pages.length} page{pages.length !== 1 ? "s" : ""} saved to this proposal.
              </p>
            </div>
          )}

          {hasUnsavedChanges && !saveError && !saveSuccess && (
            <div className="border-b border-amber-500/15 bg-amber-500/8 px-4 py-3">
              <p className="text-xs leading-5 text-amber-100/90">
                Unsaved changes — hit <strong>Save layout</strong> to persist.
              </p>
            </div>
          )}

          <div className="min-h-[860px] bg-[radial-gradient(circle_at_top,rgba(255,255,255,0.04),transparent_36%)]">
            {centerViewMode === "canvas" ? (
              <div className="p-1 md:p-2 xl:p-3">
                <ProposalBuilderCanvas
                  layout={draftLayout}
                  onChange={setDraftLayout}
                  builderMode={builderMode}
                  isSaving={false}
                  proposalContext={proposalContext}
                  inheritedPages={inheritedPages}
                  onUpdateLineItem={handleUpdateLineItem}
                  className="min-h-[820px] bg-transparent"
                />
              </div>
            ) : (
              <div className="p-4 md:p-5">
                <BuilderDocumentPreview layout={draftLayout} proposalContext={proposalContext} />
              </div>
            )}
          </div>

          <div className="border-t border-white/10 px-4 py-3">
            <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_auto_auto] md:items-center">
              <div className="min-w-0">
                <p className="text-[10px] uppercase tracking-[0.16em] text-white/35">
                  Layout source
                </p>
                <p className="mt-1 text-sm text-white/75">
                  {template?.name?.trim() ? template.name : "Template-backed layout"}
                </p>
              </div>

              <div className="rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2">
                <p className="text-[10px] uppercase tracking-[0.14em] text-white/35">
                  Mode
                </p>
                <p className="mt-1 text-sm text-white/78">
                  <Eye className="mr-1 inline h-3.5 w-3.5 text-white/45" />
                  {centerViewMode === "canvas" ? "Canvas editing" : "Full preview"}
                </p>
              </div>

              <div className="rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2">
                <p className="text-[10px] uppercase tracking-[0.14em] text-white/35">
                  Draft pages
                </p>
                <p className="mt-1 text-sm text-white/78">{pages.length}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}